import { createContext, useContext, ReactNode, useMemo } from "react";
import { useProvideAuth } from "@/hooks/useProvideAuth";
import { User, Session } from "@supabase/supabase-js";

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  orgId: string | null;
  isPlatformOwner: boolean;
  subscriptionStatus: string | null;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const authState = useProvideAuth();

  // Memoize to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      user: authState.user,
      session: authState.session,
      orgId: authState.orgId,
      isPlatformOwner: authState.isPlatformOwner,
      subscriptionStatus: authState.subscriptionStatus,
      loading: authState.loading,
      error: authState.error,
    }),
    [
      authState.user,
      authState.session,
      authState.orgId,
      authState.isPlatformOwner,
      authState.subscriptionStatus,
      authState.loading,
      authState.error,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
}
